import { useCallback, useEffect, useState } from 'react';

import type { IError as ApiError } from '@core-web/networking';
import type { IApiData, IUseApiOptions } from '../types/IApiData';
import type { IQueryData } from '../types/IQueryData';
import { useCallbackRef } from '@core-web/hooks';
import { ApiQueryCache, createApiCacheKey } from '../cache';

const DEFAULT_STALE_TIME = 5 * 60 * 1000;
const DEFAULT_CACHE_TIME = 30 * 60 * 1000;

/**
 * Use any API endpoint of your choice in your application
 * @param queryApi A function that makes the actual API call. I must either have no parameters or receive an object.
 * @param dependencies If present, API call will be repeated if any dependency changes
 * @returns Several states and methods that give you easy access to the progress and result of your call
 */
export const useApi = <TData, TQuery, TResponse>(
  queryApi: (query: TQuery) => Promise<TData>,
  options: IUseApiOptions<TQuery> = {}
): IApiData<TData, TQuery, TResponse> => {
  const [queryData, setQueryData] = useState<IQueryData<TQuery> | null>(null);
  const [data, setData] = useState<TResponse>();
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { dependencies = [], callForFile = false, cache } = options;

  const queryApiRef = useCallbackRef(queryApi);

  const cacheOptions = cache
    ? {
        key: cache.key,
        staleTime: cache.staleTime ?? DEFAULT_STALE_TIME,
        cacheTime: cache.cacheTime ?? DEFAULT_CACHE_TIME,
        enabled: cache.enabled ?? true,
      }
    : null;

  // This function wraps the API call and updates our states before and after the call
  const executeCall = useCallback(
    async (
      query: TQuery,
      throwError: boolean,
      getIsCancelled?: () => boolean,
      fileRequest?: boolean,
      forceRequest = false
    ): Promise<TData | undefined> => {
      setIsError(false);
      setIsSuccess(false);

      const cacheEnabled =
        cacheOptions?.enabled !== false && cacheOptions?.key && Boolean(cacheOptions?.key) && !fileRequest;

      const cacheKey =
        cache && cacheEnabled ? (cache?.getCacheKey?.(query) ?? createApiCacheKey(cacheOptions.key, query)) : null;

      try {
        /*
         * Avoid displaying a loader when fresh cached data is available.
         */
        const cachedData = cacheKey ? ApiQueryCache.get<TData>(cacheKey) : undefined;

        if (cachedData === undefined || forceRequest) {
          setIsLoading(true);
        }

        const apiData: any =
          cacheKey !== null
            ? await ApiQueryCache.getOrFetch<TData>(cacheKey, () => queryApiRef.current(query), {
                staleTime: cache?.staleTime ?? 5 * 60 * 1000,
                cacheTime: cache?.cacheTime ?? 30 * 60 * 1000,
                force: forceRequest,
              })
            : await queryApiRef.current(query);

        if (getIsCancelled?.()) {
          return;
        }

        if (!apiData?.data && !fileRequest) {
          throw new Error('Hmm! The operation seems unsuccessful. Please try again later!');
        }

        setData(fileRequest ? apiData : apiData.data);
        setError(null);
        setIsLoading(false);
        setIsSuccess(true);
        setIsError(false);

        return apiData;
      } catch (err: any) {
        if (getIsCancelled?.()) {
          return;
        }

        setError(err?.error ?? err);
        setIsLoading(false);
        setIsSuccess(false);
        setIsError(true);

        if (throwError) {
          throw err;
        }

        return undefined;
      }
    },
    [queryApiRef, cache?.key, cache?.enabled, cache?.staleTime, cache?.cacheTime, cache?.getCacheKey]
  );

  // This should run in two cases:
  // 1. The query parameters changed
  // 2. The dependencies we got passed in fromoutside changed
  // But we can only repeat a call that had been made before.
  // In that case we would already have some query data from when the call was made.
  useEffect(() => {
    let isCancelled = false;

    if (queryData !== null) {
      executeCall(queryData.query, false, () => isCancelled, callForFile);
    }

    return () => {
      isCancelled = true;
    };
  }, [executeCall, queryData, callForFile, ...dependencies]);

  const reset = useCallback(() => {
    setData(undefined);
    setError(null);
    setIsLoading(false);
    setIsSuccess(false);
    setIsError(false);
    setQueryData(null);
  }, []);

  const callApi = useCallback(
    (query: TQuery) => {
      // Save the current query to the state so we can repeat the query later on
      // if our dependencies change
      // Don't just save the query data as is but wrap it into an object.
      // That way the ste weill change even if the actual query data doesn't.
      // Otherwise repetitive API calls with identical query data would not be possible.
      setQueryData({ query });
    },
    [setQueryData]
  );

  const callAsyncApi = useCallback(
    async (query: TQuery): Promise<TData | undefined> => {
      return executeCall(query, true, () => false, callForFile);
    },
    [executeCall, callForFile]
  );

  const refetch = useCallback(async (): Promise<TData | undefined> => {
    if (!queryData) {
      return undefined;
    }

    return executeCall(queryData.query, false, () => false, callForFile, true);
  }, [queryData, executeCall, callForFile]);

  const invalidate = useCallback(() => {
    if (!cacheOptions?.key) {
      return;
    }

    ApiQueryCache.invalidate(cacheOptions.key);
  }, [cacheOptions?.key]);

  return {
    error,
    reset,
    callApi,
    callAsyncApi,
    data,
    isError,
    isSuccess,
    isLoading,
    refetch,
    invalidate,
  };
};
